import React, { useState, useEffect, useCallback } from 'react';
import { SERVER_URL } from '../../../constants.js'
import '../BookingCard.css';
import { Box, CircularProgress, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";

function BookingCreator(props) {
  const { selectedPerson, selectedStay, showFinancials } = props;
  const [stayId] = useState(selectedStay ? selectedStay.stayId : '0');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchPath, setSearchPath] = useState('eligableProducts');
  const [selectedDate, setSelectedDate] = useState(
    selectedStay && selectedStay.arrivalDate ? selectedStay.arrivalDate : new Date().toISOString().split('T')[0]
  );
  const [residentialProducts, setResidentialProducts] = useState([]);
  const [externalOrAfternoonProducts, setExternalOrAfternoonProducts] = useState([]);
  const [singleDayExcursion, setSingleDayExcursion] = useState([]);
  const [singleDayOther, setSingleDayOther] = useState([]);

  const [isBooking, setIsBooking] = useState(false);
  const [bookingProgress, setBookingProgress] = useState({ done: 0, total: 0 });

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const showToast = (msg, severity = "success") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  

  const fetchEligableProducts = useCallback(() => {
    
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/products/${searchPath}/stay/${stayId}`, {
        headers: { Authorization: `Bearer ${token}` },
    })
    .then(response => {
        if (response.status === 204) {
            return [];
        } else if (response.ok) {
            return response.json();
        } else {
            throw new Error('Failed to fetch bookings');
        }
    })
    .then(data => {
        const singleDay = [];
        const residentialProducts = [];
        const externalOrAfternoonProducts = [];
        const singleDayExcursion = [];
        const singleDayOther = [];

        data.forEach(product => {
          const startDate = new Date(product.startDate);
          const endDate = new Date(product.endDate);
      
          // Calculate the time difference and convert it to days
          const timeDiff = endDate - startDate;
          const dayDiff = timeDiff / (1000 * 3600 * 24);
      
          // Check if the product lasts exactly one day or starts and ends on the same day
          if (dayDiff === 1 || startDate.toDateString() === endDate.toDateString()) {
              singleDay.push(product);
      
              // Determine which category the single day product belongs to
              if (product.productType === "WEEKEND") {
                  singleDayExcursion.push(product);
              } else {
                  singleDayOther.push(product);
              }
          } else {
              // Handle products with longer durations
              switch (product.productType) {
                  case "RESIDENTIAL":
                      residentialProducts.push(product);
                      break;
                  case "EXTERNAL":
                  case "AFTERNOON":
                      externalOrAfternoonProducts.push(product);
                      break;
                  default:
                      // Handle any products that don't fit the specified categories if necessary
                      break;
              }
          }
      });
      

        // Update state with all product lists
        setResidentialProducts(residentialProducts);
        setExternalOrAfternoonProducts(externalOrAfternoonProducts);
        setSingleDayExcursion(singleDayExcursion);
        setSingleDayOther(singleDayOther);
    })
    .catch(err => {
      console.error(err);
      showToast("Failed to load eligible products. Please refresh.", "error");
    });
  }, [stayId, searchPath]);

  useEffect(() => {
  const handler = (e) => {
    if (!isBooking) return;
    e.preventDefault();
    e.returnValue = "";
  };
  window.addEventListener("beforeunload", handler);
  return () => window.removeEventListener("beforeunload", handler);
}, [isBooking]);

  useEffect(() => {
    fetchEligableProducts();
  }, [fetchEligableProducts]);

  const handleProductSelect = useCallback((product) => {
    if (selectedProducts.some((p) => p.id === product.id)) {
        setSelectedProducts(selectedProducts.filter((p) => p.id !== product.id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
}, [selectedProducts]);

  const bookOne = async (product, token) => {
  const res = await fetch(
    `${SERVER_URL}api/bookings/studentId/${selectedPerson.id}/productId/${product.id}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        actualCharge: product.defaultRate,
        bookingStatus: "PROVISIONAL",
      }),
    }
  );

  if (!res.ok) {
    let detail = "";
    try { detail = await res.text(); } catch {}
    throw new Error(`HTTP ${res.status}${detail ? `: ${detail}` : ""}`);
  }
};

const handleBookings = async () => {
  if (!selectedPerson?.id) return showToast("No student selected.", "error");
  if (!selectedProducts.length) return showToast("Select at least one product.", "info");
  if (isBooking) return;

  const token = sessionStorage.getItem("bearer");
  setIsBooking(true);
  setBookingProgress({ done: 0, total: selectedProducts.length });

  const successes = [];
  const failures = [];

  try {
    // SEQUENTIAL requests (biggest win)
    for (const product of selectedProducts) {
      try {
        await bookOne(product, token);
        successes.push(product);
      } catch (e) {
        failures.push({ product, error: e });
      } finally {
        setBookingProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    }

    // Always refresh so UI reflects reality
    await fetchEligableProducts();

    // If there were failures, verify if they *actually* got booked anyway
    // (timeouts can still commit, so we check eligibility)
    const allEligibleNow = [
      ...residentialProducts,
      ...externalOrAfternoonProducts,
      ...singleDayExcursion,
      ...singleDayOther,
    ];
    const eligibleIds = new Set(allEligibleNow.map((p) => p.id));

    const failedButNowIneligible = failures.filter((f) => !eligibleIds.has(f.product.id));

    if (failures.length === 0) {
      showToast(`Bookings created (${successes.length}/${selectedProducts.length}).`, "success");
      setSelectedProducts([]);
      return;
    }

    if (successes.length === 0 && failedButNowIneligible.length === 0) {
      showToast(`No bookings created (${failures.length} failed).`, "error");
      // keep selection so user can retry
      return;
    }

    if (failedButNowIneligible.length > 0) {
      showToast(
        `Some requests timed out, but bookings appear to have been created. Refreshed eligibility.`,
        "warning"
      );
      // remove the ones that now look booked, keep the rest for retry
      setSelectedProducts((prev) => prev.filter((p) => eligibleIds.has(p.id)));
      return;
    }

    showToast(
      `Some bookings created (${successes.length}/${selectedProducts.length}). ${failures.length} failed.`,
      "warning"
    );
    // keep failures selected for retry if you want
  } finally {
    setIsBooking(false);
    setBookingProgress({ done: 0, total: 0 });
  }
};



  const widenSearch = () => {
    console.log("widened search");
    setSearchPath('eligableProductsWide');
  }
  const normalSearch = () => {
    setSearchPath('eligableProducts');
  }

  const handleNextDay = (event) => {
    event.preventDefault(); // Prevent the default form submission
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
};

const handlePrevDay = (event) => {
    event.preventDefault(); // Prevent the default form submission
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
};    
  return(
    <React.Fragment>
      <div style={{ color: '#999999', fontSize: '10px' }}>
        <p>
          Selected Student: {selectedPerson.id} — 
          Selected Stay: {selectedStay ? `${selectedStay.arrivalDate} to ${selectedStay.departureDate}` : 'None'}
        </p>
      </div>
      <h2>Main Booking Products</h2>
      <button onClick={widenSearch} type="button">Wide Search</button>
      <button onClick={normalSearch} type="button">Normal Search</button>
      <button onClick={handleBookings} type="button" disabled={isBooking || selectedProducts.length === 0}>
  {isBooking ? "Booking..." : "Book"}
</button>
{isBooking && (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
    <CircularProgress size={18} />
    <span style={{ fontSize: 12, color: "#666" }}>
      Creating bookings… {bookingProgress.done}/{bookingProgress.total}
    </span>
  </Box>
)}
      <div className="detail-card booking-card" style={{ padding: '20px 0' }}>
        <table style={{ width: '80%', textAlign: 'left', margin: 'auto', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Product Name</th>
              <th>Product Base</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Capacity</th>
              <th>Allocated</th>
              {showFinancials && <th>Default Rate</th>}
              <th>book?</th>
            </tr>
          </thead>
          <tbody>
            {residentialProducts.map((eligableProduct) => (
              <tr key={eligableProduct.id}>
                <td>{eligableProduct.id}</td>
                <td>{eligableProduct.name}</td>
                <td>{eligableProduct.base}</td>
                <td>{eligableProduct.startDate}</td>
                <td>{eligableProduct.endDate}</td>
                <td>{eligableProduct.capacity}</td>
                <td>{eligableProduct.allocated}</td>
                {showFinancials && <td>{eligableProduct.defaultRate}</td>}
                <td>
                  <input
                    type="checkbox"
                    disabled={isBooking || eligableProduct.allocated >= eligableProduct.capacity}
                    onChange={() => handleProductSelect(eligableProduct)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Afternoon</h2>
      <div className="detail-card booking-card" style={{ padding: '20px 0' }}>
        <table style={{ width: '80%', textAlign: 'left', margin: 'auto', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Product Name</th>
              <th>Product Base</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Capacity</th>
              <th>Allocated</th>
              {showFinancials && <th>Default Rate</th>}
              <th>book?</th>
            </tr>
          </thead>
          <tbody>
            {externalOrAfternoonProducts.map((eligableProduct) => (
              <tr key={eligableProduct.id}>
                <td>{eligableProduct.id}</td>
                <td>{eligableProduct.name}</td>
                <td>{eligableProduct.base}</td>
                <td>{eligableProduct.startDate}</td>
                <td>{eligableProduct.endDate}</td>
                <td>{eligableProduct.capacity}</td>
                <td>{eligableProduct.allocated}</td>
                {showFinancials && <td>{eligableProduct.defaultRate}</td>}
                <td>
                  <input
                    type="checkbox"
                    disabled={isBooking || eligableProduct.allocated >= eligableProduct.capacity}
                    onChange={() => handleProductSelect(eligableProduct)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Weekend</h2>
      <div className="detail-card booking-card" style={{ padding: '20px 0' }}>
        <table style={{ width: '80%', textAlign: 'left', margin: 'auto', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Product Name</th>
              <th>Product Base</th>
              <th>Date</th>
              <th>Capacity</th>
              <th>Allocated</th>
              {showFinancials && <th>Default Rate</th>}
              <th>book?</th>
            </tr>
          </thead>
          <tbody>
            {singleDayExcursion.map((eligableProduct) => (
              <tr key={eligableProduct.id}>
                <td>{eligableProduct.id}</td>
                <td>{eligableProduct.name}</td>
                <td>{eligableProduct.base}</td>
                <td>{eligableProduct.startDate}</td>
                <td>{eligableProduct.capacity}</td>
                <td>{eligableProduct.allocated}</td>
                {showFinancials && <td>{eligableProduct.defaultRate}</td>}
                <td>
                  <input
                    type="checkbox"
                    disabled={isBooking || eligableProduct.allocated >= eligableProduct.capacity}
                    onChange={() => handleProductSelect(eligableProduct)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Single Day Activities</h2>
      <div className="date-selector">
    <label htmlFor="datePicker">Select Date:</label>
    <button onClick={(e) => handlePrevDay(e)}>Prev Day</button>
    <input
      type="date"
      id="datePicker"
      value={selectedDate}
      onChange={e => setSelectedDate(e.target.value)}
    />
    <button onClick={(e) => handleNextDay(e)}>Next Day</button>
</div>


      <div className="detail-card booking-card" style={{ padding: '20px 0', marginTop: '20px' }}>
        <table style={{ width: '80%', textAlign: 'left', margin: 'auto', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
            <th>ID</th>
              <th>Product Name</th>
              <th>Product Base</th>
              <th>Date</th>
              <th>Capacity</th>
              <th>Allocated</th>
              {showFinancials && <th>Default Rate</th>}
              <th>book?</th>
            </tr>
          </thead>
          <tbody>
            {singleDayOther.filter(product => 
                new Date(product.startDate).toISOString().split('T')[0] === new Date(selectedDate).toISOString().split('T')[0]
                ).length > 0 ? (
                  singleDayOther.filter(product => 
                  new Date(product.startDate).toISOString().split('T')[0] === new Date(selectedDate).toISOString().split('T')[0]
                  ).map(eligableProduct => (
                <tr key={eligableProduct.id}>
                  <td>{eligableProduct.id}</td>
                <td>{eligableProduct.name}</td>
                <td>{eligableProduct.base}</td>
                <td>{eligableProduct.startDate}</td>
                <td>{eligableProduct.capacity}</td>
                <td>{eligableProduct.allocated}</td>
                {showFinancials && <td>{eligableProduct.defaultRate}</td>}
                <td>
                    <input
                        type="checkbox"
                        checked={selectedProducts.some(p => p.id === eligableProduct.id)}
                        disabled={isBooking || eligableProduct.allocated >= eligableProduct.capacity}
                        onChange={() => handleProductSelect(eligableProduct)}
                    />
                </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">No single day products found for date {selectedDate}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button onClick={handleBookings} type="button" disabled={isBooking || selectedProducts.length === 0}>
  {isBooking ? "Creating Bookings..." : "Create Bookings"}
</button>
<Snackbar
  open={toastOpen}
  autoHideDuration={4000}
  onClose={() => setToastOpen(false)}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <MuiAlert
    onClose={() => setToastOpen(false)}
    severity={toastSeverity}
    elevation={6}
    variant="filled"
    sx={{ width: "100%" }}
  >
    {toastMessage}
  </MuiAlert>
</Snackbar>

      <div>
                <p style={{ color: '#999999', fontSize: '10px' }}>Student: {selectedPerson.id}</p>
                <p style={{ color: '#999999', fontSize: '10px' }}>Selected Stay:Selected Stay: {selectedStay ? `${selectedStay.arrivalDate} to ${selectedStay.departureDate}` : 'None'}</p>
                <p style={{ color: '#999999', fontSize: '10px' }}>Is authenticated: {sessionStorage.getItem('isAuthenticated').toString()}</p>
            </div>
            </React.Fragment>
);
}

export default BookingCreator;
